import { ChangeEvent, useEffect, useState } from "react"
import { IOmniaShell } from "@omnia/types"
import { IDashboardService } from "../types"
import { Card } from "@omnia/shared/react/Card"
import { sortBy, groupBy } from "@omnia/shared/utils/array"
import { useAsyncValue } from "@omnia/shared/react/hooks"
import { formatToReadable } from "@omnia/shared/utils/format";
import { GroupedDashboardStock } from "./model"

const TYPE_CROP_PROTECTION = "Crop protection";

interface Props {

    /** Dependency passed from Angular */
    svc: IDashboardService

    /** Dependency passed from Angular */
    shell: IOmniaShell

    /** This can be used to force the Angular host to update (ends up calling $evalAsync() on the global scope) */
    update(run?: () => void): void

}

export function StocksCard({svc, shell, update}: Props) {
    const [stockTypes, setStockTypes] = useState<string[]>([]);
    const [selectedStockType, setSelectedStockType] = useState<string>();
    
    const stocks: GroupedDashboardStock[] | undefined = useAsyncValue(() => svc.ensureInit().then(() => {
        const sortedStocks = sortBy(svc.getStocks(), ['Type', 'Name']);
        if ((sortedStocks?.length ?? 0) === 0) {
            return undefined;
        }

        return groupBy(sortedStocks, s => `${s.Type}|${s.Name}`)
            .map(({ key, values }) => {
                const first = values[0];
                const totalAmount = values.reduce((sum, item) => sum + (item.Available ?? 0), 0);
                return {
                    Type: first.Type,
                    Name: first.Name,
                    TotalAmount: totalAmount,
                    Unit: first.Unit,
                    GroupCount: values.length
                }
            }
        );
    }), [svc, svc.yearId, svc.stateVersion]);

    // update the stock types dropdown list when stocks change
    useEffect(() => {
        const types = stocks?.map(sg => sg.Type) ?? [];
        // unique values
        const uniqueTypes = [...new Set(types)];
        setStockTypes(uniqueTypes);

        // if no type is selected, set it to Crop Protection, or the first element on the list
        const cropProtectionIsPresent = uniqueTypes.includes(TYPE_CROP_PROTECTION);
        const firstElement = uniqueTypes.length > 0 ? uniqueTypes[0] : null;
        setSelectedStockType(prev => prev ?? (cropProtectionIsPresent ? TYPE_CROP_PROTECTION : firstElement));
    }, [stocks]);

    const handleStockTypeChange = (e: ChangeEvent<HTMLSelectElement>) => setSelectedStockType(e.target.value);
    
    const cardHeaderContent = <>
        {stockTypes.length > 1 && <div className="form-inline"><div className="form-group">
                <select id="groupDropdown" className="form-control" value={selectedStockType} onChange={handleStockTypeChange}>
                    {stockTypes.map(t => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
            </div></div>
        }
    </>

    const cardTools =
        <div className="btn-group">
            {svc.permissions.stocks.read &&
                <button className="btn btn-link btn-icon-toggle" onClick={() => update(() => shell.navigate("/stocks"))} title="Manage stocks">
                    <i className="fas fa-pen"></i>
                </button>
            }
        </div>

    return <Card 
        icon='icon icon-stocks' 
        title="Stocks" 
        small={false} 
        headerTools={cardTools}
        headerContent={cardHeaderContent}
        headerContentAlign="right"
        isCollapsible={true}
        >
        <div className="row">
            <table className="omnia-table">
                <thead>
                    <tr>
                        <th style={{width: '30%'}}>Type</th>
                        <th style={{width: '50%'}}>Name</th>
                        <th>Available</th>
                    </tr>
                </thead>
                <tbody>
                    {stocks?.filter((st) => !Boolean(selectedStockType) || st.Type === selectedStockType)
                        .map((stock, index) => {
                        return (
                            <tr key={index} className={stock.TotalAmount < 0 ? 'error' : ''}>
                                <td>{stock.Type}</td>
                                <td>{stock.Name}</td>
                                <td>{`${formatToReadable(stock.TotalAmount)} ${stock.Unit}`}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    </Card>
}